import type { Input } from './input.ts'
import { PAD_ACTIONS, PAD_BUTTON_NAMES, PAD_START, padBindings, type Action, type PadAction, type PadBinding } from './bindings.ts'

/**
 * Controllers, through the browser Gamepad API, on Halo 3's default layout or the player's own
 * (bindings.ts; a button can be bound as a tap and, separately, as a hold).
 *
 * In a match the pad drives `Input` through the same doors touch uses (`hold`, `tap`, `setFire`,
 * `lookBy` and the stick fields), so the game never learns which device is playing. Everywhere
 * else (the home screen, the pause screen, the results) the D-pad or left stick moves focus
 * between the buttons on screen and A presses the one in focus.
 *
 * The Gamepad API has no events for buttons or sticks, so pads are polled once a frame, and
 * only while one is connected. Browsers hide pads from a page until a button is pressed on one.
 *
 *   left stick  move · L3 crouch (held)          right stick look · R3 zoom
 *   RT fire                                      LT grenade · left gun when dual wielding · Banshee fuel rod
 *   RB reload · hold to pick up, board, exit     LB grenade type · hold to pick up left · boost in a vehicle
 *   A jump · B melee · X view / Warthog seat · Y switch weapon · D-pad ◀ grenade type · D-pad ▼ equipment
 *   Back scoreboard · Start pause
 */

/** What the pad needs to know about the match to put LT, LB and the holds where Halo 3 had them. */
export interface GamepadContext {
  /** Driving, riding or gunning. */
  seated: boolean
  /** The action the vehicle being driven boosts on while held; absent when it cannot. */
  boost?: Action
  dualWield: boolean
  /** A second gun in reach that can go in the left hand. */
  pickupLeft: boolean
}

const A = 0, B = 1, LT = 6, RT = 7, START = PAD_START
const UP = 12, DOWN = 13, LEFT = 14, RIGHT = 15

const MOVE_DEADZONE = .18
const LOOK_DEADZONE = .13
/** Radians a second at full deflection: about 190° a second across, slower up and down, as Halo's default sensitivity. */
const LOOK_YAW = 3.3
const LOOK_PITCH = 1.9
/** Look speed rises with the square of deflection, so small corrections stay small. */
const LOOK_EXPONENT = 2
/** Halo's turn acceleration: a stick held fully across for a moment speeds the turn up to this many times. */
const TURN_BOOST = 1.6
const TURN_DELAY = .18
const TURN_RAMP = .35
const TRIGGER_DOWN = .35, TRIGGER_UP = .25
/** Seconds a button must be held before it means its hold binding (pick up, board, exit) rather than its tap (reload, grenade type). */
const HOLD = .25
const NAV_THRESHOLD = .5, NAV_DELAY = .35, NAV_REPEAT = .14

let game: GamepadControls | null = null
let polling = false
let padIndex = -1
let last = 0
const prev: boolean[] = []

/** A settings screen waiting for the next button, to bind it. */
interface Capture { allowHold: boolean; done: (binding: PadBinding | null) => void; armed: boolean; button: number; held: number }
let capture: Capture | null = null
/** What B and Start do while a settings screen is open over the menus: close it, rather than start the match. */
let menuBack: (() => void) | null = null

/**
 * Bind the next controller button: `done` gets it once pressed, as a hold if it is kept down a
 * moment and `allowHold` is set, or null if Start cancels. Buttons already down are ignored until let go,
 * so the A that chose the slot is not what gets bound. Returns a cancel.
 */
export function capturePadButton(allowHold: boolean, done: (binding: PadBinding | null) => void): () => void {
  const entry: Capture = { allowHold, done, armed: false, button: -1, held: 0 }
  capture = entry
  return () => { if (capture === entry) capture = null }
}

export function setPadMenuBack(back: (() => void) | null): void { menuBack = back }

/** Start watching for controllers. Called once at page load, before any match exists. */
export function watchGamepads(): void {
  if (!('getGamepads' in navigator)) return
  addEventListener('gamepadconnected', startPolling)
  // A pad pressed before this script ran is already listed and fires no event.
  if (navigator.getGamepads().some(Boolean)) startPolling()
  for (const kind of ['keydown', 'mousedown'] as const) addEventListener(kind, () => document.body.classList.remove('pad-focus'), true)
  const style = document.createElement('style')
  style.textContent = 'body.pad-focus :focus{outline:3px solid #8fd3ff!important;outline-offset:3px}'
  document.head.append(style)
}

function startPolling(): void {
  if (polling) return
  polling = true
  last = performance.now()
  requestAnimationFrame(poll)
}

function poll(now: number): void {
  const dt = Math.min(Math.max((now - last) / 1000, 0), .1)
  last = now
  const pad = activePad()
  if (!pad) {
    if (!navigator.getGamepads().some(Boolean)) { polling = false; game?.release(); return }
  } else {
    const down = pad.buttons.map((button, i) => i === LT || i === RT
      ? button.value > (prev[i] ? TRIGGER_UP : TRIGGER_DOWN)
      : button.pressed)
    const pressed = (i: number) => !!down[i] && !prev[i]
    if (capture) { game?.release(); captureButton(capture, down, pressed, dt) }
    else if (game?.playing) game.update(pad, down, pressed, dt)
    else {
      game?.release()
      if (menuBack && (pressed(START) || pressed(B))) menuBack()
      else if (pressed(START) && game) game.input.start()
      else navigateMenus(pad, down, pressed, dt)
    }
    prev.length = 0
    prev.push(...down)
  }
  requestAnimationFrame(poll)
}

function captureButton(entry: Capture, down: boolean[], pressed: (i: number) => boolean, dt: number): void {
  const finish = (binding: PadBinding | null) => { capture = null; entry.done(binding) }
  if (!entry.armed) { entry.armed = !down.some(Boolean); return }
  if (entry.button < 0) {
    const button = PAD_BUTTON_NAMES.findIndex((_, i) => pressed(i))
    if (button < 0) return
    if (button === START) finish(null)
    else if (!entry.allowHold) finish({ button })
    else { entry.button = button; entry.held = 0 }
    return
  }
  if (!down[entry.button]) { finish({ button: entry.button }); return }
  entry.held += dt
  if (entry.held >= HOLD) finish({ button: entry.button, hold: true })
}

/** The pad last touched, so a second controller left on the desk does not steal the game. */
function activePad(): Gamepad | null {
  const pads = navigator.getGamepads()
  for (const pad of pads) {
    if (!pad?.connected || pad.index === padIndex) continue
    if (pad.buttons.some(b => b.pressed) || pad.axes.some(a => Math.abs(a) > .5)) {
      padIndex = pad.index
      prev.length = 0
    }
  }
  const pad = pads[padIndex]
  if (pad?.connected) return pad
  padIndex = pads.findIndex(p => p?.connected)
  return padIndex < 0 ? null : pads[padIndex]
}

/** Radial deadzone, rescaled so the stick still reaches 1 at the rim. Returns the direction and its 0..1 length. */
function stick(x: number, y: number, deadzone: number): [number, number, number] {
  const length = Math.hypot(x, y)
  if (length < deadzone) return [0, 0, 0]
  const magnitude = Math.min(1, (length - deadzone) / (1 - deadzone))
  return [x / length, y / length, magnitude]
}

/** Actions on foot only: in a vehicle their buttons belong to the vehicle (LB boosts). */
const FOOT_ONLY = new Set<PadAction>(PAD_ACTIONS.filter(a => a.scope === 'foot').map(a => a.id))
const HELD = new Set<PadAction>(PAD_ACTIONS.filter(a => a.held).map(a => a.id))
const PRESSED = PAD_ACTIONS.filter(a => !a.held).map(a => a.id)

export class GamepadControls {
  private readonly heldKeys = new Set<Action>()
  private moving = false
  private fire = false
  private altFire = false
  private turnHeld = 0
  /** Seconds each button has been down while it has a hold binding, or -1 once it has acted. */
  private readonly buttonHeld: number[] = []

  constructor(readonly input: Input, private readonly context: () => GamepadContext) {
    game = this
  }

  get playing(): boolean { return this.input.active }

  update(pad: Gamepad, down: boolean[], pressed: (i: number) => boolean, dt: number): void {
    const { input } = this
    const context = this.context()
    if (pad.buttons.some(b => b.pressed) || pad.axes.some(a => Math.abs(a) > MOVE_DEADZONE)) input.padActive = true
    if (pressed(START)) { this.release(); input.pause(); return }

    const [mx, my, move] = stick(pad.axes[0] ?? 0, pad.axes[1] ?? 0, MOVE_DEADZONE)
    // Only ever hand the stick back to zero once, so a thumb on the touch stick is not overwritten.
    if (move > 0 || this.moving) { input.stickX = mx * move; input.stickY = move > 0 ? -my * move : 0 }
    this.moving = move > 0

    const [lx, ly, look] = stick(pad.axes[2] ?? 0, pad.axes[3] ?? 0, LOOK_DEADZONE)
    const speed = look ** LOOK_EXPONENT
    this.turnHeld = Math.abs(lx) * look > .9 ? this.turnHeld + dt : 0
    const boost = 1 + (TURN_BOOST - 1) * Math.min(Math.max((this.turnHeld - TURN_DELAY) / TURN_RAMP, 0), 1)
    if (speed > 0) input.lookBy(-lx * speed * LOOK_YAW * boost * dt, -ly * speed * LOOK_PITCH * dt)

    const isDown = (action: PadAction) => padBindings(action).some(b => down[b.button])
    const keys = new Set<Action>()
    if (isDown('jump')) keys.add('jump')
    if (isDown('crouch')) keys.add('crouch')
    if (context.boost && isDown('boost')) keys.add(context.boost)
    for (const key of this.heldKeys) if (!keys.has(key)) input.hold(key, false)
    for (const key of keys) if (!this.heldKeys.has(key)) input.hold(key, true)
    this.heldKeys.clear()
    for (const key of keys) this.heldKeys.add(key)

    const fire = isDown('fire')
    if (fire !== this.fire) { this.fire = fire; input.setFire(fire) }
    // The grenade button is the left trigger of a dual wield.
    const altFire = isDown('grenade') && context.dualWield
    if (altFire !== this.altFire) { this.altFire = altFire; input.setAltFire(altFire) }
    input.scoreboardHeld = isDown('scoreboard')

    // Pressed actions, button by button. A button with a hold binding waits to see which is meant:
    // let go early and it is the tap (RB reloads), keep it down and it is the hold (RB picks up).
    for (let button = 0; button < down.length; button++) {
      const taps: PadAction[] = [], holds: PadAction[] = []
      for (const action of PRESSED) {
        if (!this.live(action, context)) continue
        for (const binding of padBindings(action)) if (binding.button === button) (binding.hold ? holds : taps).push(action)
      }
      const held = this.buttonHeld[button] ?? 0
      if (!holds.length) {
        if (pressed(button)) for (const action of taps) this.act(action, context)
        this.buttonHeld[button] = 0
      } else if (!down[button]) {
        if (held > 0 && held < HOLD) for (const action of taps) this.act(action, context)
        this.buttonHeld[button] = 0
      } else if (pressed(button)) {
        // A first frame of zero would read as not held; count it as a sliver of time instead.
        this.buttonHeld[button] = 1e-3
      } else if (held > 0) {
        // (A button already down when its hold became possible stays at 0 and waits to be let go.)
        if (held + dt >= HOLD) { for (const action of holds) this.act(action, context); this.buttonHeld[button] = -1 }
        else this.buttonHeld[button] = held + dt
      }
    }
  }

  /** Whether a pressed action means anything right now, so a button's tap is not kept waiting on a hold that cannot happen. */
  private live(action: PadAction, context: GamepadContext): boolean {
    if (FOOT_ONLY.has(action) && (context.seated || context.boost)) return false
    if (action === 'pickupLeft') return context.pickupLeft
    // Dual wielding, the grenade button is a trigger (above), not a throw.
    if (action === 'grenade') return !context.dualWield
    return !HELD.has(action)
  }

  private act(action: PadAction, context: GamepadContext): void {
    const { input } = this
    // Seated, the grenade button is the vehicle's second weapon: the Banshee's fuel rod.
    if (action === 'grenade') input.tap(context.seated ? 'zoom' : 'grenade')
    else if (action === 'swap') input.tap('swapBack')
    else if (action !== 'boost' && action !== 'scoreboard') input.tap(action)
  }

  /** Let go of everything the pad is holding, as when the match pauses or the pad is unplugged. */
  release(): void {
    const { input } = this
    for (const key of this.heldKeys) input.hold(key, false)
    this.heldKeys.clear()
    if (this.fire) input.setFire(false)
    if (this.altFire) input.setAltFire(false)
    if (this.moving) input.stickX = input.stickY = 0
    this.fire = this.altFire = this.moving = false
    this.buttonHeld.length = 0
    this.turnHeld = 0
    input.scoreboardHeld = false
  }
}

let navHeld = 0
let navDirection = -1

/** Move focus between the buttons on screen with the D-pad or stick; A presses, and changes a dropdown. */
function navigateMenus(pad: Gamepad, down: boolean[], pressed: (i: number) => boolean, dt: number): void {
  const ax = pad.axes[0] ?? 0, ay = pad.axes[1] ?? 0
  const direction = down[UP] || ay < -NAV_THRESHOLD ? UP : down[DOWN] || ay > NAV_THRESHOLD ? DOWN
    : down[LEFT] || ax < -NAV_THRESHOLD ? LEFT : down[RIGHT] || ax > NAV_THRESHOLD ? RIGHT : -1
  let step = false
  if (direction !== navDirection) { navDirection = direction; navHeld = 0; step = direction >= 0 }
  else if (direction >= 0) {
    navHeld += dt
    if (navHeld >= NAV_DELAY) { navHeld -= NAV_REPEAT; step = true }
  }
  const focused = document.activeElement instanceof HTMLElement && focusable(document.activeElement) ? document.activeElement : null
  if (step || pressed(A)) document.body.classList.add('pad-focus')
  if (step) {
    if (!focused) { firstTarget()?.focus(); return }
    // Left and right turn a dropdown, as they would a Halo menu option.
    if (focused instanceof HTMLSelectElement && (direction === LEFT || direction === RIGHT)) cycle(focused, direction === RIGHT ? 1 : -1)
    else nearest(focused, direction)?.focus()
    return
  }
  if (pressed(A)) {
    if (!focused) firstTarget()?.focus()
    else if (focused instanceof HTMLSelectElement) cycle(focused, 1)
    else if (!(focused instanceof HTMLInputElement && focused.type === 'text')) focused.click()
  }
}

const TARGETS = 'button, select, input:not([type=hidden]), a[href], [tabindex]:not([tabindex="-1"])'

function focusable(el: HTMLElement): boolean {
  if (el instanceof HTMLCanvasElement || (el as HTMLButtonElement).disabled || el.getAttribute('aria-disabled') === 'true') return false
  const rect = el.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return false
  const style = getComputedStyle(el)
  if (style.visibility === 'hidden' || style.pointerEvents === 'none') return false
  // In a scrolling list (the Controls screen), rows scrolled out of sight are still places to go;
  // focusing one scrolls it in. Only the list itself has to be on screen and on top.
  const list = el.parentElement?.closest<HTMLElement>('[data-pad-scroll]')
  if (list) {
    const box = list.getBoundingClientRect()
    const hit = document.elementFromPoint(box.left + box.width / 2, box.top + Math.min(box.height / 2, 20))
    return !hit || list.contains(hit)
  }
  if (rect.bottom < 0 || rect.top > innerHeight || rect.right < 0 || rect.left > innerWidth) return false
  // Covered by something on top (a closed panel behind the one that is open): not a place focus can go.
  const hit = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2)
  return !hit || el.contains(hit) || hit.contains(el)
}

function targets(): HTMLElement[] {
  return [...document.querySelectorAll<HTMLElement>(TARGETS)].filter(focusable)
}

/** Where focus lands first: the screen's main button if it marks one, else the first in reading order. */
function firstTarget(): HTMLElement | undefined {
  const all = targets()
  return all.find(el => el.matches('.primary, [autofocus]')) ?? all[0]
}

function nearest(from: HTMLElement, direction: number): HTMLElement | undefined {
  const a = from.getBoundingClientRect()
  const ax = a.left + a.width / 2, ay = a.top + a.height / 2
  const [dx, dy] = direction === UP ? [0, -1] : direction === DOWN ? [0, 1] : direction === LEFT ? [-1, 0] : [1, 0]
  let best: HTMLElement | undefined, bestScore = Infinity
  for (const el of targets()) {
    if (el === from) continue
    const b = el.getBoundingClientRect()
    const along = (b.left + b.width / 2 - ax) * dx + (b.top + b.height / 2 - ay) * dy
    if (along <= 1) continue
    const across = Math.abs((b.left + b.width / 2 - ax) * dy) + Math.abs((b.top + b.height / 2 - ay) * dx)
    const score = along + across * 2
    if (score < bestScore) { bestScore = score; best = el }
  }
  return best
}

function cycle(select: HTMLSelectElement, by: number): void {
  const count = select.options.length
  for (let i = 1; i < count; i++) {
    const index = (select.selectedIndex + by * i + count) % count
    if (select.options[index].disabled) continue
    select.selectedIndex = index
    select.dispatchEvent(new Event('change', { bubbles: true }))
    return
  }
}
